import { useEffect, useRef, useState } from 'react'
import { ArrowRight, Check, CheckCircle2, Loader2, Sparkles } from 'lucide-react'
import { Button } from '../ui'
import type { FloorPlanOptimizationResult } from '../../lib/floorPlan/rfOptimizer'
import {
  OPTIMIZATION_PHASES,
  type FloorPlanOptimizationProgress,
} from '../../lib/floorPlan/rfOptimizerProgress'

type Props = {
  progress: FloorPlanOptimizationProgress | null
  saving?: boolean
  complete?: boolean
  result?: FloorPlanOptimizationResult | null
  onDismiss?: () => void
  variant?: 'sidebar' | 'canvas'
}

function MetricCard({
  label,
  before,
  after,
  tone,
}: {
  label: string
  before: number
  after: number
  tone: 'good' | 'weak' | 'interference'
}) {
  const improved =
    tone === 'good' ? after > before : tone === 'weak' ? after < before : after <= before + 5

  const toneStyles = {
    good: {
      wrap: 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/50',
      label: 'text-emerald-800 dark:text-emerald-200',
      after: improved ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-800 dark:text-slate-100',
    },
    weak: {
      wrap: 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40',
      label: 'text-amber-900 dark:text-amber-200',
      after: improved ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-200',
    },
    interference: {
      wrap: 'border-violet-200 bg-violet-50 dark:border-violet-800 dark:bg-violet-950/40',
      label: 'text-violet-900 dark:text-violet-200',
      after: improved ? 'text-emerald-700 dark:text-emerald-300' : 'text-violet-800 dark:text-violet-200',
    },
  }[tone]

  return (
    <div className={`rounded-xl border px-3 py-2.5 ${toneStyles.wrap}`}>
      <p className={`text-[11px] font-semibold uppercase tracking-wide ${toneStyles.label}`}>{label}</p>
      <p className={`mt-1 flex items-baseline gap-1.5 text-sm font-bold tabular-nums ${toneStyles.after}`}>
        <span className="text-slate-500 dark:text-slate-400">{Math.round(before)}%</span>
        <ArrowRight size={14} className="shrink-0 text-slate-400" aria-hidden />
        <span>{Math.round(after)}%</span>
      </p>
    </div>
  )
}

function OptimizationCompletePanel({
  result,
  onDismiss,
  variant,
}: {
  result: FloorPlanOptimizationResult
  onDismiss?: () => void
  variant: 'sidebar' | 'canvas'
}) {
  const onCanvas = variant === 'canvas'
  const detailLines = result.summary.filter(
    line => !line.startsWith('Coverage:') && !line.startsWith('Interference:'),
  )

  const shellClass = onCanvas
    ? 'pointer-events-auto flex w-full max-w-lg max-h-[min(640px,90vh)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900'
    : 'mt-3 flex max-h-[min(520px,70vh)] flex-col overflow-hidden rounded-lg border border-emerald-300/80 bg-white shadow-sm dark:border-emerald-700 dark:bg-slate-900'

  return (
    <div className={shellClass} role="dialog" aria-labelledby="optimization-complete-title">
      <div
        className={`shrink-0 border-b border-slate-200 px-5 py-4 dark:border-slate-700 ${
          onCanvas ? '' : 'bg-emerald-50/80 dark:bg-emerald-950/30'
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
            <CheckCircle2 size={22} strokeWidth={2.25} />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <h2
              id="optimization-complete-title"
              className="text-base font-semibold text-slate-900 dark:text-slate-50"
            >
              Optimization complete
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              RF placement, power, and channels were updated. Review the summary below.
            </p>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <MetricCard
            label="Good coverage"
            before={result.metricsBefore.goodPct}
            after={result.metricsAfter.goodPct}
            tone="good"
          />
          <MetricCard
            label="Weak zones"
            before={result.metricsBefore.weakPct}
            after={result.metricsAfter.weakPct}
            tone="weak"
          />
          <MetricCard
            label="High interference"
            before={result.metricsBefore.highInterfPct}
            after={result.metricsAfter.highInterfPct}
            tone="interference"
          />
        </div>

        {detailLines.length > 0 && (
          <div className="mt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              What changed
            </h3>
            <ul className="mt-2 space-y-2">
              {detailLines.map(line => (
                <li
                  key={line}
                  className="flex gap-2.5 rounded-lg bg-slate-50 px-3 py-2 text-sm leading-relaxed text-slate-700 dark:bg-slate-800/60 dark:text-slate-200"
                >
                  <Check size={16} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-300">
          <span className="font-semibold text-slate-700 dark:text-slate-200">Coverage</span>{' '}
          {Math.round(result.metricsBefore.goodPct)}% → {Math.round(result.metricsAfter.goodPct)}% good cells · weak{' '}
          {Math.round(result.metricsBefore.weakPct)}% → {Math.round(result.metricsAfter.weakPct)}%.{' '}
          <span className="font-semibold text-slate-700 dark:text-slate-200">Interference</span>{' '}
          {Math.round(result.metricsBefore.highInterfPct)}% → {Math.round(result.metricsAfter.highInterfPct)}% high-overlap
          cells.
        </p>
      </div>

      {onDismiss && (
        <div className="shrink-0 border-t border-slate-200 px-5 py-4 dark:border-slate-700">
          <Button variant="primary" size="sm" type="button" className="w-full" onClick={onDismiss}>
            OK — view updated floor plan
          </Button>
        </div>
      )}
    </div>
  )
}

export default function FloorPlanOptimizationProgressPanel({
  progress,
  saving,
  complete,
  result,
  onDismiss,
  variant = 'sidebar',
}: Props) {
  const [pulse, setPulse] = useState(0)
  const listRef = useRef<HTMLUListElement>(null)
  const onCanvas = variant === 'canvas'

  useEffect(() => {
    if (!progress && !saving && !complete) return
    const id = window.setInterval(() => setPulse(p => (p + 1) % 3), 450)
    return () => window.clearInterval(id)
  }, [progress, saving, complete])

  const activeIndex = saving ? OPTIMIZATION_PHASES.length : progress ? progress.stepIndex - 1 : 0
  const barPct = saving ? 100 : progress ? Math.max(8, progress.progressPct) : 8

  useEffect(() => {
    if (complete) return
    const list = listRef.current
    if (!list) return
    const target = saving
      ? list.querySelector('[data-scroll-anchor="saving"]')
      : list.querySelector('[data-active="true"]')
    target?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [activeIndex, saving, complete, progress?.phase])

  if (complete && result) {
    return <OptimizationCompletePanel result={result} onDismiss={onDismiss} variant={variant} />
  }

  const shellClass = onCanvas
    ? 'pointer-events-auto w-full max-w-md rounded-2xl border border-slate-700 bg-slate-950/95 shadow-2xl backdrop-blur-sm'
    : 'mt-3 overflow-hidden rounded-lg border border-emerald-300/80 bg-white/90 shadow-sm dark:border-emerald-700 dark:bg-emerald-950/40'

  const headerBorder = onCanvas ? 'border-slate-700' : 'border-emerald-200/70 dark:border-emerald-800/60'

  const titleClass = onCanvas
    ? 'text-sm font-semibold text-white'
    : 'text-xs font-semibold text-emerald-900 dark:text-emerald-100'

  const subtitleClass = onCanvas
    ? 'mt-0.5 text-xs text-slate-300'
    : 'mt-0.5 truncate text-[11px] text-emerald-800/80 dark:text-emerald-200/80'

  const barTrack = onCanvas ? 'bg-slate-800' : 'bg-emerald-100 dark:bg-emerald-900/50'

  const listClass = onCanvas
    ? 'max-h-64 space-y-1 overflow-y-auto scroll-smooth px-3 py-3'
    : 'max-h-52 space-y-0.5 overflow-y-auto px-2 py-2 scroll-smooth'

  const rowText = onCanvas ? 'text-xs' : 'text-[11px]'

  return (
    <div className={shellClass} role="status" aria-live="polite" aria-busy>
      <div className={`border-b ${headerBorder} ${onCanvas ? 'px-4 py-4' : 'px-3 py-2.5'}`}>
        <div className="flex items-center gap-3">
          <div
            className={`relative flex shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white ${
              onCanvas ? 'h-10 w-10' : 'h-8 w-8'
            }`}
          >
            <Sparkles size={onCanvas ? 18 : 16} className="animate-pulse" />
            <span className="absolute inset-0 animate-ping rounded-lg bg-emerald-400/30" />
          </div>
          <div className="min-w-0 flex-1">
            <p className={titleClass}>Optimizing your floor plan</p>
            <p className={subtitleClass}>
              {saving
                ? 'Saving AP positions and settings…'
                : progress?.detail ?? progress?.label ?? 'Starting RF analysis…'}
            </p>
          </div>
        </div>

        <div className={`mt-3 overflow-hidden rounded-full ${barTrack} ${onCanvas ? 'h-2' : 'mt-2.5 h-1.5'}`}>
          <div
            className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-300 transition-[width] duration-700 ease-out"
            style={{ width: `${barPct}%` }}
          />
        </div>
      </div>

      <ul ref={listRef} className={listClass}>
        {OPTIMIZATION_PHASES.map((phase, i) => {
          const done = saving || i < activeIndex
          const active = !saving && i === activeIndex

          return (
            <li
              key={phase.id}
              data-active={active ? 'true' : undefined}
              className={`flex items-start gap-2.5 rounded-lg px-2.5 py-2 transition-all duration-300 ${rowText} ${
                active
                  ? onCanvas
                    ? 'bg-slate-800 text-white ring-1 ring-emerald-500/50'
                    : 'bg-emerald-100/90 text-emerald-950 dark:bg-emerald-900/50 dark:text-emerald-50'
                  : done
                    ? onCanvas
                      ? 'text-slate-300'
                      : 'text-emerald-800/90 dark:text-emerald-200/90'
                    : onCanvas
                      ? 'text-slate-500'
                      : 'text-mg-muted/70'
              }`}
            >
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                {done ? (
                  <Check size={onCanvas ? 14 : 13} className="text-emerald-400" strokeWidth={2.5} />
                ) : active ? (
                  <Loader2 size={onCanvas ? 14 : 13} className="animate-spin text-emerald-300" />
                ) : (
                  <span
                    className={`rounded-full ${onCanvas ? 'h-2 w-2 bg-slate-600' : 'h-1.5 w-1.5 bg-mg-border'}`}
                  />
                )}
              </span>
              <span className="min-w-0 flex-1 leading-snug">
                <span className={active ? 'font-semibold' : done ? 'font-medium' : ''}>{phase.label}</span>
                {active && progress?.detail && (
                  <span
                    className={`mt-0.5 block font-normal ${
                      onCanvas ? 'text-[11px] text-slate-400' : 'text-[10px] text-emerald-800/75 dark:text-emerald-200/75'
                    }`}
                  >
                    {progress.detail}
                    <span className="inline-block w-4 text-left tabular-nums">{'.'.repeat(pulse + 1)}</span>
                  </span>
                )}
              </span>
            </li>
          )
        })}

        {saving && (
          <li
            data-scroll-anchor="saving"
            className={`flex items-start gap-2.5 rounded-lg px-2.5 py-2 ${rowText} ${
              onCanvas
                ? 'bg-slate-800 text-white ring-1 ring-emerald-500/50'
                : 'bg-emerald-100/90 text-emerald-950 dark:bg-emerald-900/50 dark:text-emerald-50'
            }`}
          >
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
              <Loader2 size={onCanvas ? 14 : 13} className="animate-spin text-emerald-300" />
            </span>
            <span className="font-semibold leading-snug">
              Saving to floor plan
              <span
                className={`mt-0.5 block font-normal ${onCanvas ? 'text-[11px] text-slate-400' : 'text-[10px] opacity-80'}`}
              >
                Writing positions, TX power, and mount settings
                <span className="inline-block w-4 text-left tabular-nums">{'.'.repeat(pulse + 1)}</span>
              </span>
            </span>
          </li>
        )}
      </ul>
    </div>
  )
}
